import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
// hooks
import useResponsive from '../../../hooks/useResponsive';
// sections
import FormDialog from './FormDialog';
// components
import Breadcrumbs from '../../../components/breadcrumbs';
import { Label, ButtonField, ButtonFields, Message as FormMessage } from '../../../components/hook-form';
import Dialog from '../../../components/dialog';
// @form
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @devextreme
import DateBox from 'devextreme-react/date-box';
import { Template } from 'devextreme-react/core/template';
// @moment
import moment from 'moment-timezone';
// utils
import { timezones } from '../../../utils/format';
import { REGEXP_ID, REGEXP_ID_START } from '../../../utils/regexp';
// assets
import Calendar from '../../../assets/button/Calendar';
// styles
import { Container, ButtonWrapper, PrimaryButton, TertiaryButton, FormTabs, Tabs, TempContent, Message } from '../../../styles';
// apis
import { getOrganizations, existsId, createMembers, updateMembers } from '../../../apis';

// ----------------------------------------------------------------------

const utc = new Date(timezones(moment().utc().format(), 'YYYY-MM-DDTHH:mm:ss'));
const current = new Date(utc).setFullYear(utc.getFullYear() + 1);
const expired = [{ value: '6', name: '6개월' }, { value: '1', name: '1년' }, { value: '2', name: '2년' }, { value: 'setting', name: '직접 설정' }];

const schema = yup.object().shape({
  identification: yup.string()
    .required()
    .trim()
    .matches(REGEXP_ID_START, '첫 글자는 영문 소문자를 사용해 주세요.')
    .matches(REGEXP_ID, '형식을 확인해 주세요.')
});

// ----------------------------------------------------------------------

const size = 10;

// ----------------------------------------------------------------------

export default function FormPage() {
  const mobile = useResponsive('down', 'md');

  const navigate  = useNavigate();
  const create = useLocation().pathname.split('/')[3] === 'create' ? true : false;
  const state = useLocation().state;
  const admin = state?.role === '관리자' ? 'ADMIN' : 'USER';

  const {
    control,
    getValues,
    setFocus,
    setError,
    clearErrors,
    handleSubmit
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      identification: state?.identification || ''
    }
  });
  
  const [ data, setData ] = useState({ content: [], totalElements: 0, totalPages: 1 });
  const { content: rows, totalElements: total, totalPages: pages } = data;

  const [ name, setName ] = useState('');
  const [ page, setPage ] = useState(0);

  const [ visible, setVisible ] = useState(false);

  const [ organization, setOrganization ] = useState({id: state? state.organizationId : null, name: state? state.organization : ''});
  const [ errors, setErrors ] = useState(null);

  const [ type, setType ] = useState('6');
  const [ date, setDate ] = useState(new Date(current));
  const [ difference, setDifference ] = useState(null);

  useEffect(() => {
    if ( create ) {
      ( async () =>
        await getOrganizations(setData, name, page, size)
      )();
    }
  }, [create]);

  useEffect(() => {
    if ( errors !== null ) {
      if ( organization.id ) return setErrors(false);
      setErrors(true);
    }
  }, [organization]);

  useEffect(() => {
    if ( type !== 'setting') {
      if ( type === '6' ) {
        const half = new Date(utc).setMonth(utc.getMonth() + 6);
        return setDate(new Date(half));
      }
      const year = new Date(utc).setFullYear(utc.getFullYear() + Number(type));
      setDate(new Date(year));
    }
  }, [type]);

  const handleDate = (value: any) => {
    let difference = Math.abs( utc.getTime() - value.getTime() );
    difference = Math.ceil( difference / (1000 * 60 * 60 * 24) );
    setDifference(difference);
    setDate(value);
  };

  const handlePage = (value: number) => {
    setPage(value-1);
    getOrganizations(setData, name, value-1, size);
  };

  const handleSearch = () => {
    if ( name.length > 0 && name.replace(/ /g, '').length === 0 ) return;
    setPage(0);
    getOrganizations(setData, name, 0, size);
  };

  const [ role, setRole ] = useState(state ? admin : 'USER');

  const [ checkId, setCheckId ] = useState({result: null, message: null});
  const [ checkedId, setCheckedId ] = useState(null);
  
  const [ message, setMessage ] = useState(null);

  const handleIdentification = async () => {
    const identification = getValues('identification').trim();
    if ( identification === '' ) {
      setError('identification', {type: 'required'});
      setFocus('identification');
    } else if ( !REGEXP_ID.test(identification) ) {
      setError('identification', {type: 'matches', message: '형식을 확인해 주세요.'});
      setFocus('identification');
    } else {
      clearErrors('identification');
      await existsId(identification)
        .then((response) => {
          setCheckId({result: response, message: response ? '이미 등록된 아이디입니다.' : '사용하실 수 있는 아이디입니다.'});
          setCheckedId(identification);
        })
        .catch(() => {
          setCheckId({result: null, message: '나중에 다시 시도해 주세요.'});
        });
    }
  };

  const [ success, setSuccess ] = useState({result: false, answer: null });
  const handleClose = () => {
    setSuccess({result: false, answer: null });
    navigate('/accounts/members', {state: {submit: true}});
  };

  const onSubmit = async (data: { identification?: string; }) => {
    if ( create ) {
      if ( organization.id && checkId.result === false && checkedId === data.identification && !moment(date).isBefore (timezones(moment().utc().format(), 'YYYY-MM-DDTHH'), 'day') ) {
        await createMembers(organization.id, data.identification, role, date)
          .then((response) => {
            setSuccess({result: true, answer: response });
          })
          .catch(() => setMessage('사용자 계정을 등록할 수 없습니다. 나중에 다시 시도해 주세요.'));
      }

      if ( !organization.id ) setErrors(true);

      if ( checkId.result === null || checkedId !== data.identification ) {
        setCheckId({result: null, message: '중복 체크 후 진행하실 수 있습니다.'});
      }

    } else if ( !create ) {
      await updateMembers(state.memberId, role)
        .then(() => navigate('/accounts/members', {state: {submit: true}}))
        .catch(() => setMessage('사용자 계정을 수정할 수 없습니다. 나중에 다시 시도해 주세요.'));
    }
  };

  return (
    <Container>
      <Breadcrumbs
        heading={`계정 ${create ? '등록' : '수정'}`}
        links={[
          {name: '계정 관리'},
          {name: '사용자', link: '/accounts/members'},
          {name: `계정 ${create ? '등록' : '수정'}`}
        ]}
      />

      <form onSubmit={ handleSubmit(onSubmit) }>
        <div>
          <Label>{create && <span>* </span>}조직</Label>
          <ButtonFields $error={errors !== null ? errors : false}>
            <input type='text' value={organization.name} placeholder='조직을 선택해 주세요.' disabled />
            <TertiaryButton type='button' disabled={!create ? true : false} onClick={() => setVisible(true)}>검색</TertiaryButton>
          </ButtonFields>
          { errors === true &&
            <FormMessage>조직을 선택해 주세요.</FormMessage>
          }
        </div>

        <ButtonField
          name='identification'
          label='아이디'
          essential={create ? true : false}
          description='3~20자 사이 영문 소문자, 숫자, 특수기호 -_ 를 사용해 주세요.'
          disabled={!create ? true : false}
          control={control}
          exists={checkId}
          action={
            <TertiaryButton type='button' disabled={!create ? true : false} onClick={() => handleIdentification()}>중복 체크</TertiaryButton>
          }
        />

        <div>
          <Label><span>* </span>권한</Label>
          <select value={role} onChange={(event) => setRole(event.target.value)}>
            <option value='USER'>사용자</option>
            <option value='MANAGER'>매니저</option>
            <option value='ADMIN'>관리자</option>
          </select>
        </div>

        <div>
          <Label>{create && <span>* </span>}계정 만료일</Label>

          <FormTabs>
            <Tabs>
              { expired.map((item) => (
                <button key={item.value} type='button' onClick={() => setType(item.value)} className={type === item.value ? 'active' : ''} disabled={!create ? true : false}>{item.name}</button>
              ))}
            </Tabs>
            <p>총 사용 기간: { difference }일</p>
          </FormTabs>

          <DateBox
            type='date'
            value={date}
            onValueChanged={(event) => handleDate(event.value)}
            displayFormat='yyyy-MM-dd'
            openOnFieldClick={true}
            useMaskBehavior={true}
            pickerType={mobile ? 'rollers' : 'calendar'}
            min={utc}
            disabled={!create || type !== 'setting' ? true : false}
            dropDownButtonTemplate='calendar'
          >
            <Template name='calendar' render={Calendar} />
          </DateBox>
          { moment(date).isBefore (timezones(moment().utc().format(), 'YYYY-MM-DDTHH'), 'day') &&
            <FormMessage style={{'marginTop': '4px'}}>현재일보다 이후로 선택해 주세요.</FormMessage>
          }
        </div>

        <Message $visible={message ? true : false}>{ message }</Message>

        <ButtonWrapper>
          <PrimaryButton type='submit'>{create ? '등록' : '수정'}</PrimaryButton>
          <TertiaryButton type='button' onClick={() => navigate('/accounts/members')}>취소</TertiaryButton>
        </ButtonWrapper>
      </form>

      { visible &&
        <FormDialog
          rows={rows}
          setName={setName}
          page={page}
          pages={pages}
          size={size}
          handlePage={handlePage}
          handleSearch={handleSearch}
          setVisible={setVisible}
          organization={organization}
          setOrganization={setOrganization}
        />
      }

      { success.result &&
        <Dialog
          title='계정 등록 완료'
          handleClose={handleClose}
        >
          <p>
            계정 등록이 완료되었습니다.<br />
            임시 비밀번호는 발급된 시점부터 24시간만 유효합니다.<br />
            임시 비밀번호를 사용해서 로그인하신 후<br />
            비밀번호를 변경하셔야 정상적으로 로그인이 가능합니다.
          </p>
          <TempContent>
            <p>
              아이디<br />
              임시 비밀번호
            </p>
            <span>
              { success.answer.identification }<br/>
              { success.answer.temporaryPassword }
            </span>
          </TempContent>

          <PrimaryButton onClick={() => handleClose()}>확인</PrimaryButton>
        </Dialog>
      }
    </Container>
  );
}